import React from 'react';
import { useState, useRef } from 'react';
import { FileCheck, Upload, FileSpreadsheet, HelpCircle, X, AlertTriangle } from 'lucide-react';

const FileUpload = ({ onFileUpload }: { onFileUpload: (file: File) => void }) =>
{
    const [ fileName, setFileName ] = useState<string>('');
    const [ isLoading, setIsLoading ] = useState<boolean>(false);
    const [ isDragging, setIsDragging ] = useState<boolean>(false);
    const [ isHovering, setIsHovering ] = useState<boolean>(false);
    const [ fileError, setFileError ] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement> | React.DragEvent<HTMLDivElement>) =>
    {
        let file: File | undefined;
        if ('dataTransfer' in e) {
            file = e.dataTransfer.files?.[0];
        } else {
            file = e.target.files?.[0];
        }
        
        setFileError(null);
        
        if (!file) {
            return;
        }
        
        const validFormats = ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        
        if (!validFormats.includes(file.type)) {
            setFileError(`El formato del archivo ${file.name} no es válido. Por favor, utilice un archivo CSV.`);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            return;
        }
        
        // if (fileExtension !== 'csv') {
        //     setFileError(`Advertencia: El servidor actualmente solo procesa archivos CSV correctamente. Su archivo ${fileExtension?.toUpperCase()} podría no ser compatible.`);
        // }
        
        setIsLoading(true);
        setFileName(file.name);
        onFileUpload(file);
        setIsLoading(false);
    };

    const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleMouseEnter = () => {
        setIsHovering(true);
    };

    const handleMouseLeave = () => {
        setIsHovering(false);
    };

    const removeFile = (e: React.MouseEvent) => {
        e.stopPropagation();
        setFileName('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <div>
            {!fileName ? (
                <div 
                    className={`mt-2 flex flex-col justify-center px-6 pt-5 pb-6 border-2 
                        ${isDragging 
                            ? 'border-blue-500 bg-blue-50' 
                            : isHovering 
                                ? 'border-blue-300 bg-blue-50' 
                                : 'border-gray-300'} 
                        border-dashed rounded-md transition-colors duration-200 cursor-pointer`} 
                    onClick={handleClick} 
                    onDrop={(e) => { e.preventDefault(); handleFileChange(e); setIsDragging(false); }} 
                    onDragOver={(e) => e.preventDefault()} 
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                >
                    <div className="space-y-1 text-center">
                        <svg
                            className={`mx-auto h-12 w-12 ${isHovering || isDragging ? 'text-blue-500' : 'text-gray-400'} transition-colors duration-200`}
                            stroke="currentColor"
                            fill="none"
                            viewBox="0 0 48 48"
                            aria-hidden="true"
                        >
                            <path
                                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                strokeWidth={2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                        <div className="flex text-sm text-gray-600 justify-center">
                            <label
                                htmlFor="file-upload"
                                className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                            >
                                <span>Seleccionar archivo</span>
                                <input
                                    id="file-upload"
                                    name="file-upload"
                                    type="file"
                                    className="sr-only"
                                    accept=".csv,.xls,.xlsx"
                                    onChange={handleFileChange}
                                    ref={fileInputRef}
                                />
                            </label>
                            <p className="pl-1"> o arrastre y suelte</p>
                        </div>
                        <p className="text-xs text-gray-500">CSV, XLS o XLSX hasta 10MB</p>
                    </div>
                </div>
            ) : (
                <div className="mt-2">
                    {fileError && (
                        <div className="mb-3 bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded">
                            <div className="flex items-start">
                                <div className="flex-shrink-0">
                                    <AlertTriangle className="h-5 w-5 text-yellow-400" />
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm text-yellow-700">
                                        {fileError}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="border-2 border-gray-300 rounded-md p-4">
                    {isLoading ? (
                        <div className="flex items-center text-sm text-gray-600">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span>Subiendo archivo...</span>
                        </div>
                    ) : (
                        <div className="flex items-center justify-between">
                            <div className="flex items-center text-sm text-gray-600">
                                <FileSpreadsheet className="mr-2 h-5 w-5 text-green-500" />
                                <span className="font-medium">{fileName}</span>
                            </div>
                            <button 
                                onClick={removeFile} 
                                className="p-1 rounded-full hover:bg-gray-200 transition-colors duration-200"
                                title="Eliminar archivo"
                            >
                                <X className="h-5 w-5 text-gray-500" />
                            </button>
                        </div>
                    )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default FileUpload;